import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import AnimateHeight from 'react-animate-height';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';

import { useIsMobile } from '../../../core/hooks/use-is-mobile';
import { useKeyGenerator } from '../../../core/hooks/use-key-generator';
import { useLocale } from '../../../core/hooks/use-locale';
import { useScroll } from '../../hooks/use-scroll';
import { cloneDeep, delay } from '../../utils/functions';
import { Icon, iconAfter, iconBefore } from '../../utils/icons';
import HTMLRender from '../html-render/html-render';
import Tooltip from '../tooltip/tooltip';

import { useStyles } from './accordion-styles';
import { AccordionHandle, AccordionProps } from './accordion-types';

const ANIMATION_DURATION = 300;

const AccordionLocal: ForwardRefRenderFunction<
  AccordionHandle,
  AccordionProps
> = (
  {
    items,
    itemClasses,
    nameClasses,
    customStyle,
    scroll = null,
    arrow = 'end',
  },
  ref
) => {
  const { Message } = useLocale();

  const generateKey = useKeyGenerator();

  const styles = useStyles(customStyle ?? {});

  /* ------------------------------------------------------ */

  const isMobile = useIsMobile();

  /* ------------------------------------------------------ */

  const [itemsState, setItemsState] = useState(
    items?.map(({ active }) => ({
      active: active ?? false,
      current: false,
    }))
  );

  /**
   * Indication that accordion should scroll on next render.
   */
  const [shouldScroll, setShouldScroll] = useState(!!scroll);

  const handleChange = useCallback(
    async (index?: number, action?: 'open' | 'close') => {
      const newItemsState = cloneDeep(itemsState)?.map((state, i) => {
        const open = () => {
          state.active = true;
          state.current = true;
        };
        const close = () => {
          state.active = false;
          state.current = true;
        };
        const unsetCurrent = () => {
          state.current = false;
        };

        /**
         * Close all.
         */
        if (index === undefined) {
          close();
          return state;
        }

        /**
         * Unset current for other items.
         */
        if (i !== index) {
          unsetCurrent();
          return state;
        }

        /**
         * Perform action.
         */
        switch (action) {
          case 'open':
            open();
            break;
          case 'close':
            close();
            break;
          default:
            state.active ? close() : open();
            break;
        }

        return state;
      });

      setItemsState(newItemsState);

      if (
        !isEqual(
          itemsState?.map(({ active }) => active),
          newItemsState?.map(({ active }) => active)
        )
      ) {
        await delay(ANIMATION_DURATION);
        setShouldScroll(!!scroll);
      }
    },
    [itemsState, scroll]
  );

  /* ------------------------------------------------------ */

  useImperativeHandle(ref, () => ({
    open: async (index) => await handleChange(index, 'open'),
    close: async (index) => await handleChange(index, 'close'),
    closeAll: async () => await handleChange(),
  }));

  /* ------------------------------------------------------ */

  const current = useMemo(
    () => itemsState?.findIndex(({ current }) => current),
    [itemsState]
  );

  const currentRef = useRef<HTMLDivElement>(null);

  const scrollToCurrent = useScroll(currentRef);

  /* ------------------------------------------------------ */

  /**
   * Perform scroll.
   */
  useEffect(() => {
    if (!shouldScroll) {
      return;
    }

    setShouldScroll(false);
    scrollToCurrent();
  }, [scrollToCurrent, shouldScroll]);

  /* ------------------------------------------------------ */

  if (!items?.length) {
    return null;
  }

  

  return (
    <div className="accordion">
      {items.map(({ name, content }, index) => {
        return (
          <div 
            key={generateKey(index)}
            {...(index === current && { ref: currentRef })}
            className={classNames('accordion__item', styles.item, itemClasses, {
              'accordion__item--active': itemsState && itemsState[index].active,
            })}
          >
            <Tooltip
              title={itemsState && itemsState[index].active ? `${Message.PACK}` : `${Message.UNPACK}`}
              placement={isMobile ? 'top' : 'right-end'}
            >
              <button
                type="button"
                onClick={() => handleChange(index)}
                aria-expanded={itemsState && itemsState[index].active}
                className={classNames('accordion__name', nameClasses, {
                  [iconBefore(Icon.ANGLE_DOWN, null)]: arrow === 'start',
                  [iconAfter(Icon.ANGLE_DOWN, null)]: arrow === 'end',
                })}
                disabled={content === undefined}
              >
                {typeof name === 'string' ? <HTMLRender html={name} /> : name}
              </button>
            </Tooltip>
            {content !== undefined && (
              <AnimateHeight
                duration={ANIMATION_DURATION}
                height={itemsState && (itemsState[index].active ? 'auto' : 0)}
                animateOpacity
              >
                {typeof content === 'string' ? (
                  <div className="accordion__content">
                    <HTMLRender html={content} />
                  </div>
                ) : (
                  content
                )}
              </AnimateHeight>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Accordion = forwardRef(AccordionLocal);

export default Accordion;
